import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import * as fileMgmt from '../../../file-mgmt'
import type * as me from '../../../me'
import type { Feature } from '../feature'

/**
 * Effect that listens to uploadFile action, handles file upload to file management,
 * and relays to saveData to persist the file name to the node record.
 */
export const onUploadFile = store.effects.create(
  (
    feature: Feature,
    fileMgmtFeature: fileMgmt.Feature,
    meFeature: me.Feature,
  ) =>
    feature.actions.uploadFile.relay((forward, action) => {
      const { file, chartId } = action

      return rxjs
        .combineLatest([
          meFeature.contexts.selectors.zoneContext.asObservable,
          feature.selectors.getEditingNode({ chartId }).asObservable,
        ])
        .pipe(
          rxjs.take(1),
          rxjs.switchMap(([context, editingNode]) => {
            if (!context) {
              return rxjs.of({
                cancellation: {
                  reason: 'Context not available',
                },
              })
            }

            if (!editingNode) {
              return rxjs.of({
                cancellation: {
                  reason: 'No editing node found',
                },
              })
            }

            const { values, modelId } = editingNode

            if (!values) {
              return rxjs.of({
                cancellation: {
                  reason: 'No values on editing node',
                },
              })
            }

            const { extension } = fileMgmt.models.FileRequest.getFileInfo(file)

            const recordId = values[ents.core.dms.data.Record.Id.KEY]
            const resumeFileName = `${values['!name'] as string}.${extension}`

            return fileMgmtFeature.actions.upload
              .from('TreeEditor.UploadFile.Effect')
              .execute({
                context,
                file,
                nameResolver: () => resumeFileName,
                // TODO: We probably don't want to do this, but we will see what we can do.
                // id: recordId,
                purpose: ents.core.common.fileManagement.Purpose.FileManagement,
                qualifier: recordId,
              })
              .asObservable.pipe(
                rxjs.take(1),
                rxjs.map(() =>
                  forward(feature.actions.saveData, {
                    input: {
                      chartId,
                      modelId,
                      record: {
                        purposes: [],
                        values: {
                          ...values,
                          '!resume_file': resumeFileName,
                        },
                      },
                    },
                  }),
                ),
                rxjs.catchError((error: Error) =>
                  rxjs.of({
                    error,
                  }),
                ),
              )
          }),
        )
    }).asObservable,
)
